import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { Badge, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { AuthorityEnum } from 'src/shared/models/enums/authority.enum';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { SysProductResponse } from '../models/sysProductResponse';

type UserCreateModalPermissionButtonProps = {
  sysProduct: SysProductResponse | undefined;
  index: number;
  authorityStatus: number;
  disableSysProducts?: SysProductResponse[];
};

export const authorityArray: SelectableItem[] = [
  // {
  //   value: `${AuthorityEnum.Extern}`,
  //   label: 'user:AuthorityEnum.Extern',
  // },
  {
    value: `${AuthorityEnum.Guest}`,
    label: 'user:AuthorityEnum.Guest',
  },
  {
    value: `${AuthorityEnum.User}`,
    label: 'user:AuthorityEnum.User',
  },
  {
    value: `${AuthorityEnum.Privileged}`,
    label: 'user:AuthorityEnum.Privileged',
  },
  {
    value: `${AuthorityEnum.Administrator}`,
    label: 'user:AuthorityEnum.Administrator',
  },

  // {
  //   value: `${AuthorityEnum.SuperAdmin}`,
  //   label: 'user:AuthorityEnum.SuperAdmin',
  // },
];

export const UserCreateModalPermissionButton = (props: UserCreateModalPermissionButtonProps) => {
  const { sysProduct, index, authorityStatus, disableSysProducts } = props;
  const { t } = useTranslation(['user']);
  const {
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [validateAuthority, setValidateAuthority] = useState<number>(authorityStatus);

  const { control } = useFormContext();
  const { remove: removeInvoiceGroup } = useFieldArray({ control, name: 'invoiceGroups' });

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, ix: string) => {
    const authority = Number(ix);
    setSelectedIndex(authority);
    setOpen(false);
    setValue(`sysProducts.${index}.authority`, ix);
    setValue(`sysProducts.${index}.isChecked`, true);
    if (errors?.sysProducts?.[index]?.authority) {
      clearErrors(`sysProducts.${index}.authority`);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleIsChecked = (checkedState: boolean) => {
    setIsChecked(checkedState);
    setValue(`sysProducts.${index}.isChecked`, checkedState);
    if (!checkedState) {
      setValue(`sysProducts.${index}.authority`, 0);
      if (sysProduct?.yvName.toLowerCase() === SysProductYvNameEnum.Invoice.toLowerCase()) {
        removeInvoiceGroup();
      }
    }
  };

  const formStateIsChecked = useWatch({ name: `sysProducts.${index}.isChecked` });
  const formStateAuthority = useWatch({ name: `sysProducts.${index}.authority` });

  useEffect(() => {
    if (formStateIsChecked) setIsChecked(formStateIsChecked);
    if (formStateAuthority) setSelectedIndex(formStateAuthority);
  }, [formStateAuthority, formStateIsChecked, getValues, index]);

  useEffect(() => {
    if (sysProduct?.yvName.toLowerCase() === SysProductYvNameEnum.Invoice.toLowerCase() && validateAuthority > AuthorityEnum.Privileged) {
      setValidateAuthority(AuthorityEnum.Privileged);
    }
  }, [sysProduct?.yvName, validateAuthority]);

  if (!sysProduct) return null;

  const disableSysProduct = disableSysProducts?.some((item) => item.product_id === sysProduct?.product_id);
  const authorityLabel = authorityArray.find((item) => String(item.value) === String(formStateAuthority))?.label;

  return (
    <Box sx={{ mb: 1 }}>
      <ButtonGroup variant={isChecked ? 'contained' : 'outlined'} ref={anchorRef} size='small' fullWidth disableElevation>
        <Button
          onClick={() => handleIsChecked(!isChecked)}
          size='small'
          fullWidth
          sx={{ justifyContent: 'space-between', fontSize: '1rem', pl: 2, pr: 2 }}
          disabled={disableSysProduct}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                sx={{
                  '&.Mui-checked': {
                    color: (thm) => thm.palette.primary.contrastText,
                  },
                  '&.MuiCheckbox-root': {
                    padding: '5px 10px 5px 5px !important',
                  },
                }}
                onClick={() => handleIsChecked(!isChecked)}
                disabled={disableSysProduct}
              />
            }
            onClick={() => handleIsChecked(!isChecked)}
            label={sysProduct.name}
            sx={{ whiteSpace: 'nowrap', pointerEvents: 'none' }}
          />
          <Typography
            color={isChecked ? 'inherit' : 'text.disabled'}
            sx={{
              whiteSpace: 'nowrap',
            }}
          >
            {isChecked && t(authorityLabel || '')}
            {isChecked && errors?.sysProducts?.[index]?.authority && (
              <Badge
                badgeContent={t('user:createUser.authoritySelectionError')}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    borderRadius: '4px',
                    padding: '0.5em 1em',
                    position: 'relative',
                    transform: 'none',
                  },
                }}
              />
            )}
          </Typography>
        </Button>
        <Button
          size='small'
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup='menu'
          onClick={handleToggle}
          sx={{ maxWidth: '2rem' }}
          disabled={disableSysProduct}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>

      <Popper
        sx={{
          zIndex: (thm) => thm.zIndex.modal + 10,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement='bottom-end'
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom',
            }}
          >
            <Paper elevation={3}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu' autoFocusItem sx={{ minWidth: '12rem' }}>
                  {authorityArray
                    .filter((item) => Number(item.value) <= validateAuthority)
                    .map((option, ix) => (
                      <MenuItem
                        key={option.value}
                        selected={ix === selectedIndex}
                        onClick={(event) => handleMenuItemClick(event, option.value)}
                        disabled={disableSysProduct}
                      >
                        {t(option.label)}
                      </MenuItem>
                    ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};
